import { useEffect, useMemo, useState } from 'react';
import { Table, Space, Button, Modal, Form, Input, Select, DatePicker, Tag, message, Collapse, Typography } from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, CarOutlined } from '@ant-design/icons';
import {
  supabase,
  formatDate,
  formatDateTime,
  compressImage,
  validateImageFile,
  useAuthUser,
  parseUserRole,
  isAdminRole,
  usePortalPermissions,
  useFleetOperator,
  VEHICLE_FEATURE_OPTIONS,
  formatVehicleAdminLabel,
  vehicleComplianceReadiness,
  summarizeActiveCompliance,
  complianceTagColor,
  complianceReadinessRank,
  isExpiryPast,
} from '@ubus/shared';
import type { Vehicle, Driver, FleetOperator, VehicleFeature, ComplianceReadiness } from '@ubus/shared';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import EntityDrawer from '../components/EntityDrawer';
import AdminListPage from '../components/AdminListPage';
import ComplianceSummaryBar from '../components/ComplianceSummaryBar';
import StatusTag from '../components/StatusTag';
import { TableRowActions } from '../components/TableRowActions';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { localeConfirm } from '../utils/localeModal';
import { formatAdminDbError } from '../utils/formatAdminDbError';

type VehicleStatus = Vehicle['status'];

function confirmActiveExpired(
  t: TFunction,
  opts: {
    title: string;
    content: string;
  }
): Promise<boolean> {
  return new Promise((resolve) => {
    localeConfirm(t, {
      title: opts.title,
      content: opts.content,
      okType: 'danger',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

function Vehicles() {
  const { t, i18n } = useTranslation();
  const user = useAuthUser();
  const isHqAdmin = isAdminRole(parseUserRole(user));
  const { operator, operatorId } = useFleetOperator();
  const { hasPermission } = usePortalPermissions();
  const canCreate = hasPermission('vehicles.create');
  const canEdit = hasPermission('vehicles.edit');
  const canDelete = hasPermission('vehicles.delete');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [operators, setOperators] = useState<FleetOperator[]>([]);
  const operatorNameById = useMemo(() => {
    const map = Object.fromEntries(operators.map((o) => [o.id, o.name]));
    if (operator) map[operator.id] = operator.name;
    return map;
  }, [operators, operator]);
  const driverNameById = useMemo(
    () => Object.fromEntries(drivers.map((d) => [d.id, d.name])),
    [drivers]
  );
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [viewVehicle, setViewVehicle] = useState<Vehicle | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewStats, setViewStats] = useState<{ tripCount: number; driverCount: number } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [complianceFilter, setComplianceFilter] = useState<ComplianceReadiness | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [form] = Form.useForm();
  const watchedOperatorId = Form.useWatch('fleet_operator_id', form) as string | undefined;
  const filterOperatorId = isHqAdmin ? watchedOperatorId : (operatorId ?? undefined);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  const preferredDriverOptions = useMemo(
    () =>
      drivers
        .filter((d) => !filterOperatorId || d.fleet_operator_id === filterOperatorId)
        .filter((d) => d.status === 'active')
        .map((d) => ({ value: d.id, label: d.name })),
    [drivers, filterOperatorId]
  );

  const vehiclesWithReadiness = useMemo(
    () =>
      vehicles.map((v) => ({
        ...v,
        readiness: vehicleComplianceReadiness(v),
      })),
    [vehicles]
  );

  const complianceSummary = useMemo(
    () => summarizeActiveCompliance(vehiclesWithReadiness),
    [vehiclesWithReadiness]
  );

  const handleComplianceChip = (readiness: ComplianceReadiness | undefined) => {
    setComplianceFilter(readiness);
    // Keep chip counts and list口径 aligned: compliance scan = active only
    if (readiness) setStatusFilter('active');
  };

  const filteredVehicles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return vehiclesWithReadiness.filter((v) => {
      // Compliance chip scopes to active (same as summary)
      if (complianceFilter) {
        if (v.status !== 'active') return false;
        if (v.readiness !== complianceFilter) return false;
      } else if (statusFilter && v.status !== statusFilter) {
        return false;
      }
      if (!q) return true;
      const plate = (v.registration_plate || '').toLowerCase();
      const fleet = v.fleet_number.toLowerCase();
      const model = (v.model || '').toLowerCase();
      return plate.includes(q) || fleet.includes(q) || model.includes(q);
    });
  }, [vehiclesWithReadiness, statusFilter, complianceFilter, searchQuery]);

  const hasListFilters = !!statusFilter || !!complianceFilter || !!searchQuery.trim();

  const featureOptions = useMemo(
    () =>
      VEHICLE_FEATURE_OPTIONS.map((feature) => ({
        value: feature,
        label: t(`vehicles.features.${feature}`),
      })),
    [t]
  );

  useEffect(() => {
    fetchVehicles();
    void supabase
      .from('drivers')
      .select('*')
      .order('name')
      .then(({ data }) => setDrivers(data ?? []));
    if (isHqAdmin) {
      void supabase
        .from('fleet_operators')
        .select('*')
        .order('name')
        .then(({ data }) => setOperators(data ?? []));
    }
  }, [isHqAdmin]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        message.error(error.message);
      } else {
        setVehicles(data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchVehicles();
  }, []);

  const handleCreate = () => {
    setEditingVehicle(null);
    form.resetFields();
    if (!isHqAdmin && operatorId) {
      form.setFieldsValue({ fleet_operator_id: operatorId });
    }
    setPhotoFile(null);
    setPhotoPreview(null);
    setModalVisible(true);
  };

  const handleEdit = (record: Vehicle) => {
    setEditingVehicle(record);
    form.setFieldsValue({
      ...record,
      registration_plate: record.registration_plate ?? '',
      features: record.features ?? [],
      preferred_driver_id: record.preferred_driver_id ?? undefined,
      registration_expiry: record.registration_expiry ? dayjs(record.registration_expiry) : null,
      insurance_expiry: record.insurance_expiry ? dayjs(record.insurance_expiry) : null,
      last_service_at: record.last_service_at ? dayjs(record.last_service_at) : null,
      hvis_last_at: record.hvis_last_at ? dayjs(record.hvis_last_at) : null,
      hvis_next_due: record.hvis_next_due ? dayjs(record.hvis_next_due) : null,
    });
    setPhotoFile(null);
    setPhotoPreview(typeof record.photo_url === 'string' ? record.photo_url : null);
    setModalVisible(true);
  };

  const handleView = async (record: Vehicle) => {
    setViewVehicle(record);
    setViewStats(null);
    setViewLoading(true);

    try {
      const { data: trips, error } = await supabase
        .from('trips')
        .select('driver_id')
        .eq('vehicle_id', record.id);

      if (error) {
        message.error(error.message);
      } else {
        const driverIds = new Set(
          (trips || []).map((t) => t.driver_id).filter(Boolean)
        );
        setViewStats({
          tripCount: trips?.length ?? 0,
          driverCount: driverIds.size,
        });
      }
    } finally {
      setViewLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    localeConfirm(t, {
      title: t('vehicles.messages.deleteTitle'),
      content: t('vehicles.messages.deleteContent'),
      okText: t('vehicles.actions.delete'),
      okType: 'danger',
      onOk: async () => {
        const { error } = await supabase
          .from('vehicles')
          .delete()
          .eq('id', id);

        if (error) {
          message.error(formatAdminDbError(error, i18n.language, 'delete'));
        } else {
          message.success(t('vehicles.messages.deleted'));
          fetchVehicles();
        }
      }
    });
  };

  const uploadPhoto = async (file: File): Promise<string | null> => {
    setPhotoUploading(true);
    try {
      const compressed = await compressImage(file);
      const fileName = `vehicle-photos/${Date.now()}-${Math.random().toString(36).substring(2)}.jpg`;

      const { error } = await supabase.storage
        .from('vehicles')
        .upload(fileName, compressed, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        message.error(t('vehicles.messages.photoUploadFailed', { message: error.message }));
        return null;
      }

      const { data: urlData } = supabase.storage
        .from('vehicles')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('vehicles.messages.photoUploadFailedGeneric'));
      return null;
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleStatusChange = async (id: string, status: VehicleStatus) => {
    const vehicle = vehicles.find((v) => v.id === id);
    if (
      status === 'active' &&
      vehicle &&
      isExpiryPast(vehicle.registration_expiry)
    ) {
      const ok = await confirmActiveExpired(t, {
        title: t('vehicles.messages.activeExpiredConfirmTitle'),
        content: t('vehicles.messages.activeExpiredConfirmContent'),
      });
      if (!ok) return;
    }

    const { error } = await supabase
      .from('vehicles')
      .update({
        status,
        status_updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) {
      message.error(error.message);
    } else {
      message.success(t('vehicles.messages.statusUpdated'));
      void fetchVehicles();
    }
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      const registrationExpiry = values.registration_expiry
        ? (values.registration_expiry as Dayjs).format('YYYY-MM-DD')
        : null;
      const toYmd = (v: unknown) =>
        v ? (v as Dayjs).format('YYYY-MM-DD') : null;
      const nextStatus = String(values.status ?? 'active');
      if (nextStatus === 'active' && isExpiryPast(registrationExpiry)) {
        const ok = await confirmActiveExpired(t, {
          title: t('vehicles.messages.activeExpiredConfirmTitle'),
          content: t('vehicles.messages.activeExpiredConfirmContent'),
        });
        if (!ok) return;
      }

      let photoUrl: string | null = editingVehicle?.photo_url || null;
      if (photoFile) {
        const uploadedUrl = await uploadPhoto(photoFile);
        if (uploadedUrl) {
          photoUrl = uploadedUrl;
        }
      }

      const vehicleData = {
        ...values,
        photo_url: photoUrl,
        capacity: Number(values.capacity),
        year: Number(values.year),
        registration_plate: String(values.registration_plate ?? '').trim(),
        features: (values.features as VehicleFeature[] | undefined) ?? [],
        preferred_driver_id: (values.preferred_driver_id as string | undefined) || null,
        registration_expiry: registrationExpiry,
        insurance_expiry: toYmd(values.insurance_expiry),
        last_service_at: toYmd(values.last_service_at),
        hvis_last_at: toYmd(values.hvis_last_at),
        hvis_next_due: toYmd(values.hvis_next_due),
      };

      if (editingVehicle) {
        const editedId = editingVehicle.id;
        const { error } = await supabase
          .from('vehicles')
          .update(vehicleData)
          .eq('id', editedId);

        if (error) {
          message.error(error.message);
        } else {
          message.success(t('vehicles.messages.updated'));
          setModalVisible(false);
          await afterMutation(editedId, fetchVehicles);
        }
      } else {
        const { data: created, error } = await supabase
          .from('vehicles')
          .insert([vehicleData])
          .select('id')
          .single();

        if (error) {
          message.error(error.message);
        } else {
          message.success(t('vehicles.messages.created'));
          setModalVisible(false);
          await afterMutation(created?.id, fetchVehicles);
        }
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('vehicles.messages.saveFailed'));
    }
  };

  const handlePhotoChange = async (file: File) => {
    if (!validateImageFile(file)) {
      message.error(t('vehicles.messages.onlyImages'));
      return false;
    }

    try {
      const compressed = await compressImage(file);
      setPhotoFile(compressed);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(compressed);
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('vehicles.messages.invalidImage'));
    }
    return false;
  };

  type VehicleRow = Vehicle & { readiness: ComplianceReadiness };

  const columns: TableProps<VehicleRow>['columns'] = [
    {
      title: t('vehicles.columns.identity'),
      key: 'identity',
      width: 180,
      fixed: 'left',
      sorter: (a, b) => {
        const ap = (a.registration_plate || a.fleet_number).toLowerCase();
        const bp = (b.registration_plate || b.fleet_number).toLowerCase();
        return ap.localeCompare(bp);
      },
      render: (_, record) => {
        const plate = record.registration_plate?.trim();
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{plate || t('vehicles.plateMissing')}</div>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{record.fleet_number}</div>
          </div>
        );
      },
    },
    {
      title: t('vehicles.columns.readiness'),
      dataIndex: 'readiness',
      key: 'readiness',
      width: 120,
      defaultSortOrder: 'ascend',
      sorter: (a, b) =>
        complianceReadinessRank(a.readiness) - complianceReadinessRank(b.readiness),
      render: (readiness: ComplianceReadiness) => (
        <Tag color={complianceTagColor(readiness)}>{t(`compliance.${readiness}`)}</Tag>
      ),
    },
    {
      title: t('vehicles.columns.photo'),
      key: 'photo',
      width: 70,
      render: (_, record) =>
        record.photo_url ? (
          <img
            src={record.photo_url}
            alt={record.fleet_number}
            style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }}
          />
        ) : (
          <div style={{ width: 40, height: 40, borderRadius: 4, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CarOutlined style={{ color: '#bbb' }} />
          </div>
        )
    },
    {
      title: t('vehicles.columns.features'),
      dataIndex: 'features',
      key: 'features',
      width: 160,
      render: (features: string[] | undefined) =>
        features?.length ? (
          <Space size={[4, 4]} wrap>
            {features.map((feature) => (
              <Tag key={feature}>{t(`vehicles.features.${feature}`, { defaultValue: feature })}</Tag>
            ))}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      title: t('vehicles.columns.preferredDriver'),
      dataIndex: 'preferred_driver_id',
      key: 'preferred_driver_id',
      width: 140,
      ellipsis: true,
      render: (id: string | null | undefined) => (id ? driverNameById[id] ?? '—' : '—'),
    },
    {
      title: t('vehicles.columns.model'),
      dataIndex: 'model',
      key: 'model',
      ellipsis: true,
      width: 140,
    },
    {
      title: t('vehicles.columns.capacity'),
      dataIndex: 'capacity',
      key: 'capacity',
      width: 90,
      render: (val: number) => t('vehicles.seats', { count: val })
    },
    {
      title: t('vehicles.columns.registrationExpiry'),
      dataIndex: 'registration_expiry',
      key: 'registration_expiry',
      width: 130,
      render: (val: string | undefined) => (val ? formatDate(val) : '—'),
      sorter: (a, b) => (a.registration_expiry || '').localeCompare(b.registration_expiry || ''),
    },
    {
      title: t('vehicles.columns.operator'),
      dataIndex: 'fleet_operator_id',
      key: 'fleet_operator_id',
      ellipsis: true,
      width: 140,
      render: (id: string) => operatorNameById[id] ?? '—',
    },
    {
      title: t('vehicles.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: VehicleStatus, record) =>
        canEdit ? (
          <Select
            value={status}
            size="small"
            style={{ width: 120 }}
            onChange={(value) => void handleStatusChange(record.id, value)}
            options={[
              { value: 'active', label: t('vehicles.status.active') },
              { value: 'maintenance', label: t('vehicles.status.maintenance') },
              { value: 'retired', label: t('vehicles.status.retired') },
            ]}
          />
        ) : (
          <StatusTag status={status} />
        ),
    },
    {
      title: t('vehicles.columns.acceptingTasks'),
      dataIndex: 'accepting_tasks',
      key: 'accepting_tasks',
      width: 100,
      render: (v: boolean | undefined) => (
        <Tag color={v ? 'success' : 'default'}>
          {v ? t('vehicles.accepting.yes') : t('vehicles.accepting.no')}
        </Tag>
      ),
      filters: [
        { text: t('vehicles.accepting.yes'), value: true },
        { text: t('vehicles.accepting.no'), value: false },
      ],
      onFilter: (value, record) => Boolean(record.accepting_tasks) === value,
    },
    {
      title: t('vehicles.columns.actions'),
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <TableRowActions>
          <Button type="link" icon={<EyeOutlined />} onClick={() => void handleView(record)}>
            {t('vehicles.actions.view')}
          </Button>
          {canEdit ? (
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              {t('vehicles.actions.edit')}
            </Button>
          ) : null}
          {canDelete ? (
            <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
              {t('vehicles.actions.delete')}
            </Button>
          ) : null}
        </TableRowActions>
      ),
    },
  ];

  return (
    <>
      <AdminListPage
        title={t('vehicles.title')}
        subtitle={t('vehicles.subtitle')}
        extra={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('vehicles.add')}
            </Button>
          ) : undefined
        }
        summary={
          <ComplianceSummaryBar
            summary={complianceSummary}
            activeFilter={complianceFilter}
            onSelect={handleComplianceChip}
          />
        }
        filters={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder={t('vehicles.placeholders.search')}
              style={{ width: 240 }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={setSearchQuery}
            />
            <Select
              placeholder={t('vehicles.placeholders.allStatuses')}
              allowClear
              style={{ minWidth: 160 }}
              value={statusFilter}
              disabled={!!complianceFilter}
              onChange={(value) => {
                setStatusFilter(value);
                if (value && value !== 'active') setComplianceFilter(undefined);
              }}
              options={[
                { value: 'active', label: t('vehicles.status.active') },
                { value: 'maintenance', label: t('vehicles.status.maintenance') },
                { value: 'retired', label: t('vehicles.status.retired') },
              ]}
            />
          </Space>
        }
        resultCount={filteredVehicles.length}
        hasActiveFilters={hasListFilters}
        onClearFilters={() => {
          setStatusFilter(undefined);
          setComplianceFilter(undefined);
          setSearchQuery('');
        }}
      >
        <Table
          dataSource={filteredVehicles}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onChange={onTableChange}
          rowClassName={rowClassName}
          scroll={{ x: 1400 }}
          locale={{
            emptyText: hasListFilters ? t('vehicles.emptyFiltered') : t('vehicles.empty'),
          }}
        />
      </AdminListPage>

      <Modal
        title={editingVehicle ? t('vehicles.edit') : t('vehicles.add')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText={editingVehicle ? t('vehicles.actions.save') : t('vehicles.actions.create')}
        cancelText={t('vehicles.actions.cancel')}
        width={560}
        confirmLoading={photoUploading}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(v) => void handleSubmit(v)} requiredMark>
          {/* Habit order: plate → expiry → model/seats → fleet # (AU ops verbal flow) */}
          <Form.Item
            name="registration_plate"
            label={t('vehicles.fields.registrationPlate')}
            rules={[{ required: true, message: t('vehicles.messages.plateRequired') }]}
          >
            <Input
              placeholder={t('vehicles.placeholders.registrationPlate')}
              autoFocus={!editingVehicle}
            />
          </Form.Item>
          <Form.Item
            name="registration_expiry"
            label={t('vehicles.fields.registrationExpiry')}
            rules={[
              {
                required: true,
                message: t('vehicles.validation.registrationExpiry'),
              },
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="insurance_expiry" label={t('vehicles.fields.insuranceExpiry')}>
            <DatePicker style={{ width: '100%' }} allowClear />
          </Form.Item>
          <Form.Item name="last_service_at" label={t('vehicles.fields.lastServiceAt')}>
            <DatePicker style={{ width: '100%' }} allowClear />
          </Form.Item>
          <Form.Item name="hvis_last_at" label={t('vehicles.fields.hvisLastAt')}>
            <DatePicker style={{ width: '100%' }} allowClear />
          </Form.Item>
          <Form.Item name="hvis_next_due" label={t('vehicles.fields.hvisNextDue')}>
            <DatePicker style={{ width: '100%' }} allowClear />
          </Form.Item>
          <Form.Item
            name="model"
            label={t('vehicles.fields.model')}
            rules={[{ required: true, message: t('vehicles.validation.model') }]}
          >
            <Input placeholder={t('vehicles.placeholders.model')} />
          </Form.Item>
          <Form.Item
            name="capacity"
            label={t('vehicles.fields.capacity')}
            rules={[{ required: true, message: t('vehicles.validation.capacity') }]}
          >
            <Input type="number" min={1} max={100} placeholder={t('vehicles.placeholders.capacity')} />
          </Form.Item>
          <Form.Item
            name="year"
            label={t('vehicles.fields.year')}
            rules={[{ required: true, message: t('vehicles.validation.year') }]}
          >
            <Input type="number" min={1990} max={2100} placeholder={t('vehicles.placeholders.year')} />
          </Form.Item>
          <Form.Item
            name="fleet_number"
            label={t('vehicles.fields.fleetNumber')}
            rules={[{ required: true, message: t('vehicles.validation.fleetNumber') }]}
          >
            <Input placeholder={t('vehicles.placeholders.fleetNumber')} disabled={!!editingVehicle} />
          </Form.Item>

          {/* Operator: quiet read-only on create; HQ Select only when editing */}
          {editingVehicle && isHqAdmin ? (
            <Form.Item
              name="fleet_operator_id"
              label={t('vehicles.fields.operator')}
              rules={[{ required: true, message: t('vehicles.validation.operator') }]}
            >
              <Select
                options={operators.map((o) => ({ value: o.id, label: o.name }))}
                placeholder={t('vehicles.placeholders.operator')}
                onChange={() => form.setFieldsValue({ preferred_driver_id: undefined })}
              />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="fleet_operator_id"
                hidden
                rules={[{ required: true, message: t('vehicles.validation.operator') }]}
              >
                <Input type="hidden" />
              </Form.Item>
              <Typography.Paragraph type="secondary" style={{ marginTop: -4, marginBottom: 16 }}>
                {t('vehicles.operatorReadonly', {
                  name:
                    (isHqAdmin
                      ? operators.find((o) => o.id === watchedOperatorId)?.name
                      : operator?.name) || '—',
                })}
              </Typography.Paragraph>
            </>
          )}

          {editingVehicle ? (
            <Form.Item
              name="status"
              label={t('vehicles.fields.status')}
              initialValue="active"
            >
              <Select
                options={[
                  { value: 'active', label: t('vehicles.status.active') },
                  { value: 'maintenance', label: t('vehicles.status.maintenance') },
                  { value: 'retired', label: t('vehicles.status.retired') },
                ]}
              />
            </Form.Item>
          ) : (
            <Form.Item name="status" hidden initialValue="active">
              <Input type="hidden" />
            </Form.Item>
          )}

          <Collapse
            ghost
            style={{ marginLeft: -12 }}
            defaultActiveKey={editingVehicle ? ['advanced'] : undefined}
            items={[
              {
                key: 'advanced',
                label: (
                  <Typography.Text style={{ fontWeight: 500 }}>
                    {t('vehicles.formSections.advanced')}
                  </Typography.Text>
                ),
                children: (
                  <>
                    {isHqAdmin && !editingVehicle ? (
                      <Form.Item label={t('vehicles.changeOperator')}>
                        <Select
                          value={watchedOperatorId}
                          options={operators.map((o) => ({ value: o.id, label: o.name }))}
                          placeholder={t('vehicles.placeholders.operator')}
                          onChange={(value) => {
                            form.setFieldsValue({
                              fleet_operator_id: value,
                              preferred_driver_id: undefined,
                            });
                          }}
                        />
                      </Form.Item>
                    ) : null}
                    <Form.Item
                      name="features"
                      label={t('vehicles.fields.features')}
                      initialValue={[]}
                    >
                      <Select
                        mode="multiple"
                        allowClear
                        options={featureOptions}
                        placeholder={t('vehicles.placeholders.features')}
                      />
                    </Form.Item>
                    <Form.Item
                      name="preferred_driver_id"
                      label={t('vehicles.fields.preferredDriver')}
                      extra={
                        preferredDriverOptions.length === 0
                          ? t('vehicles.emptyPreferredDriver')
                          : undefined
                      }
                    >
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={preferredDriverOptions}
                        placeholder={t('vehicles.placeholders.preferredDriver')}
                        disabled={preferredDriverOptions.length === 0}
                      />
                    </Form.Item>
                    <Form.Item name="notes" label={t('vehicles.fields.notes')}>
                      <Input.TextArea rows={3} placeholder={t('vehicles.placeholders.notes')} />
                    </Form.Item>
                    <Form.Item label={t('vehicles.fields.photo')}>
                      <div style={{ marginBottom: 8 }}>
                        {photoPreview && (
                          <img
                            src={photoPreview}
                            alt={t('vehicles.preview')}
                            style={{
                              width: '100%',
                              maxHeight: 200,
                              objectFit: 'cover',
                              borderRadius: 8,
                              marginBottom: 8,
                            }}
                          />
                        )}
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void handlePhotoChange(file);
                          }}
                        />
                      </div>
                    </Form.Item>
                  </>
                ),
              },
            ]}
          />
        </Form>
      </Modal>

      <EntityDrawer
        open={!!viewVehicle}
        onClose={() => setViewVehicle(null)}
        title={viewVehicle ? t('vehicles.detailsTitle', { number: viewVehicle.fleet_number }) : t('vehicles.details')}
        loading={viewLoading}
        items={
          viewVehicle
            ? [
                {
                  key: 'fleet',
                  label: t('vehicles.columns.fleetNumber'),
                  children: formatVehicleAdminLabel(viewVehicle, t('vehicles.plateMissing')),
                },
                {
                  key: 'registration_plate',
                  label: t('vehicles.fields.registrationPlate'),
                  children: viewVehicle.registration_plate?.trim() || '—',
                },
                {
                  key: 'readiness',
                  label: t('vehicles.columns.readiness'),
                  children: (
                    <Tag color={complianceTagColor(vehicleComplianceReadiness(viewVehicle))}>
                      {t(`compliance.${vehicleComplianceReadiness(viewVehicle)}`)}
                    </Tag>
                  ),
                },
                { key: 'model', label: t('vehicles.columns.model'), children: viewVehicle.model },
                { key: 'capacity', label: t('vehicles.columns.capacity'), children: t('vehicles.seats', { count: viewVehicle.capacity }) },
                { key: 'year', label: t('vehicles.columns.year'), children: viewVehicle.year },
                {
                  key: 'features',
                  label: t('vehicles.fields.features'),
                  children: viewVehicle.features?.length ? (
                    <Space size={[4, 4]} wrap>
                      {viewVehicle.features.map((feature) => (
                        <Tag key={feature}>{t(`vehicles.features.${feature}`, { defaultValue: feature })}</Tag>
                      ))}
                    </Space>
                  ) : (
                    '—'
                  ),
                },
                {
                  key: 'preferred_driver',
                  label: t('vehicles.fields.preferredDriver'),
                  children: viewVehicle.preferred_driver_id
                    ? driverNameById[viewVehicle.preferred_driver_id] ?? '—'
                    : '—',
                },
                {
                  key: 'registration_expiry',
                  label: t('vehicles.columns.registrationExpiry'),
                  children: viewVehicle.registration_expiry ? formatDate(viewVehicle.registration_expiry) : '—',
                },
                {
                  key: 'insurance_expiry',
                  label: t('vehicles.fields.insuranceExpiry'),
                  children: viewVehicle.insurance_expiry ? formatDate(viewVehicle.insurance_expiry) : '—',
                },
                {
                  key: 'last_service_at',
                  label: t('vehicles.fields.lastServiceAt'),
                  children: viewVehicle.last_service_at ? formatDate(viewVehicle.last_service_at) : '—',
                },
                {
                  key: 'hvis_last_at',
                  label: t('vehicles.fields.hvisLastAt'),
                  children: viewVehicle.hvis_last_at ? formatDate(viewVehicle.hvis_last_at) : '—',
                },
                {
                  key: 'hvis_next_due',
                  label: t('vehicles.fields.hvisNextDue'),
                  children: viewVehicle.hvis_next_due ? formatDate(viewVehicle.hvis_next_due) : '—',
                },
                {
                  key: 'status',
                  label: t('vehicles.columns.status'),
                  children: <StatusTag status={viewVehicle.status} />,
                },
                {
                  key: 'accepting_tasks',
                  label: t('vehicles.columns.acceptingTasks'),
                  children: (
                    <Tag color={viewVehicle.accepting_tasks ? 'success' : 'default'}>
                      {viewVehicle.accepting_tasks
                        ? t('vehicles.accepting.yes')
                        : t('vehicles.accepting.no')}
                    </Tag>
                  ),
                },
                {
                  key: 'accepting_tasks_at',
                  label: t('vehicles.columns.acceptingTasksAt'),
                  children: viewVehicle.accepting_tasks_at
                    ? formatDateTime(viewVehicle.accepting_tasks_at)
                    : '—',
                },
                {
                  key: 'status_updated',
                  label: t('vehicles.fields.statusUpdated'),
                  children: viewVehicle.status_updated_at ? formatDateTime(viewVehicle.status_updated_at) : '—',
                },
                { key: 'notes', label: t('vehicles.fields.notes'), children: viewVehicle.notes || '—' },
                { key: 'created', label: t('vehicles.columns.created'), children: formatDateTime(viewVehicle.created_at) },
                {
                  key: 'updated',
                  label: t('vehicles.columns.updated'),
                  children: formatDateTime(viewVehicle.updated_at),
                },
                { key: 'trips', label: t('vehicles.fields.linkedTrips'), children: viewStats?.tripCount ?? '—' },
                { key: 'drivers', label: t('vehicles.fields.linkedDrivers'), children: viewStats?.driverCount ?? '—' },
                ...(viewVehicle.photo_url
                  ? [{
                      key: 'photo',
                      label: t('vehicles.columns.photo'),
                      children: (
                        <img
                          src={viewVehicle.photo_url}
                          alt={viewVehicle.fleet_number}
                          style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 8 }}
                        />
                      ),
                    }]
                  : []),
              ]
            : undefined
        }
      />
    </>
  );
}

export default Vehicles;
